import React, { Fragment, PureComponent } from 'react';
import { createPortal } from 'react-dom';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { get, throttle, debounce } from 'lodash';
import { Row, Col, Button, Alert } from 'reactstrap';
import generate from 'shortid';
import * as Yup from 'yup';

import {
  GET_CREATE_TEMPLATE_URL, getCompanies, getEsignDocs, getCompany,
  toggleDrawer, GET_ESIGN_DOCS, setSelectedDocument, getEditTemplateUrl, getDocumentPreviewUrl,
  displayToaster, DELETE_COMPANY_DOCUMENT, LOADING_PROP_TYPE,
  CREATE_SIGNATURE_REQUEST, clearLoadingState, getTenantEsignatureData,
  GET_EMPLOYEE_DOCUMENT_CATEGORIES, updateCompanyDocument, getCompanyDocCategories,
} from '../../modules';
import { CompanyDocumentsTable, NoContent, Spinner, SearchBar, SendForSignatures } from '../../components';
import { EditDocument, CreateTemplate } from '..';
import { FILTER_TABLE_DEBOUNCE, FETCH_TABLE_SEARCH_DEBOUNCE, MAX_DOC_CATEGORY_LENGTH } from '../../constants';
import { esignProductTierNames } from '../../constants/esignProductTierNames';

class CompanyDocuments extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      target: document.getElementById('company-documents-ui'),
      nextPage: '',
      mode: '',
      selectedCompanyId: get(window, 'asure.appSettings.selectedCompany.companyId'),
      paginating: true,
      docFilter: '',
      filtering: false,
      searching: false,
      isModalOpen: false,
      latestSearch: '',
      fetchActive: '',
      unfilteredNextPage: '',
    };
    this.throttledScroll = throttle(this.handleScroll.bind(this), 500);
    this.showSendSignatureModal = this.showSendSignatureModal.bind(this);
    this.editTemplate = this.editTemplate.bind(this);
  }

  componentDidMount() {
    const { target, nextPage: next, selectedCompanyId } = this.state;
    window.addEventListener('scroll', this.throttledScroll);
    if (target) {
      this.props.getTenantEsignatureData();
      // NOTE: if the user has selected a company through ADHR 2.0, we prevent the user
      // from changing to a different company by disabling the dropdown. In this case,
      // we don't need to get a list of available companies.
      if (!selectedCompanyId) {
        this.props.getCompanies();
      } else {
        this.props.getEsignDocs(next);
        this.props.getCompany(selectedCompanyId);
      }
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.selectedCompanyId && this.state.selectedCompanyId) {
      this.props.getEsignDocs();
    }
    if (this.props.loading[DELETE_COMPANY_DOCUMENT] === 'complete' && prevProps.loading[DELETE_COMPANY_DOCUMENT] === 'loading') {
      this.props.displayToaster([
        'success',
        <FormattedMessage id="DOCUMENT_DELETED" />,
        'app',
        5000,
        true,
      ]);
    }
    if (this.state.mode && !this.props.drawerIsOpen && prevProps.drawerIsOpen) {
      this.resetDrawerMode();
    }
    if (prevProps.loading[CREATE_SIGNATURE_REQUEST] === 'loading' && this.props.loading[CREATE_SIGNATURE_REQUEST] === 'complete') {
      const { selectedDocument: { title }, sentSignatureRequests } = this.props;
      const signatureRequestCount = sentSignatureRequests.length;
      this.props.displayToaster([
        'success',
        <FormattedMessage
          id="SIGNATURE_REQUEST_SENT_TO_EMPLOYEES"
          values={{
            title,
            employeeCount: signatureRequestCount,
            plural: signatureRequestCount > 1 ? 's' : '',
          }}
          defaultMessage="SIGNATURE_REQUEST_SENT"
        />,
        'app',
        5000,
        true,
      ]);
      this.handleSendSignatureModal();
    }
    if (this.state.fetchActive !== this.state.latestSearch &&
        this.props.loading.currentRequests[GET_ESIGN_DOCS].includes(this.state.latestSearch)) {
      this.setFetchActive();
    }
    if (this.state.filtering &&
        this.state.fetchActive === this.state.latestSearch &&
        !this.props.loading.currentRequests[GET_ESIGN_DOCS].includes(this.state.latestSearch)) {
      this.unsetFiltering();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledScroll);
  }

  onSubmit = (category, title, isPublishedToEmployee) => {
    const { selectedDocument } = this.props;
    this.props.updateCompanyDocument(
      {},
      selectedDocument.companyId,
      title,
      category,
      isPublishedToEmployee,
      selectedDocument.id,
      selectedDocument.isOnboardingDocument,
    );
  }

  setFetchActive() {
    this.setState({ fetchActive: this.state.latestSearch });
  }

  resetDrawerMode = () => {
    this.setState({ mode: '' });
  }

  toggleSendSignatureModal() {
    this.setState({ mode: 'sendSignature' });
    this.setState({ isModalOpen: !this.state.isModalOpen });
  }

  handleSendSignatureModal() {
    this.toggleSendSignatureModal();
    this.props.clearLoadingState(CREATE_SIGNATURE_REQUEST);
  }

  showSendSignatureModal(templateId) {
    const { docs: { results: documents } } = this.props;
    const document = documents.filter(doc => doc.id === templateId)[0];

    this.setState({ mode: 'sendSignature' });
    this.setState({ isModalOpen: true }, () => {
      this.props.setSelectedDocument(document);
    });
  }

  toggleCreateTemplateModal(close) {
    this.setState({ mode: 'create' });
    this.setState({ isModalOpen: !close });
  }

  unsetFiltering() {
    this.setState({ filtering: false });
  }

  groupCategories = () => {
    const { docCategories: { recent, categories } } = this.props;
    return [
      { label: 'RECENT', options: recent },
      { label: 'CATEGORIES', options: categories },
    ];
  };

  loadMoreCategories = () => {
    const { docCategories } = this.props;
    if (docCategories.next && this.props.loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'complete') {
      this.props.getDocCategoryList(docCategories.next, this.props.selectedDocument.companyId);
    }
  };

  editTemplate(templateId) {
    const { docs: { results: documents }, getEsignatureDocsLoading } = this.props;
    const document = documents.filter(doc => doc.id === templateId)[0];

    // Prevent edit functionality while we're loading data
    if (getEsignatureDocsLoading === 'loading') return;
    const { state } = this;
    this.props.setSelectedDocument(document);
    this.setState({
      ...state,
      mode: 'edit',
    }, () => {
      this.props.getDocumentPreviewUrl('company', document.id);
      this.props.toggleDrawer(true);
    });
  }

  searchDocs(query, requestId) {
    if (query) {
      this.props.getEsignDocs('', query, requestId);
    } else {
      this.props.getEsignDocs(this.state.unfilteredNextPage);
    }
  }

  debounceSearchDocs = debounce(this.searchDocs, (FETCH_TABLE_SEARCH_DEBOUNCE - FILTER_TABLE_DEBOUNCE));

  handleSearch(query) {
    if (query !== this.state.docFilter) {
      const requestId = generate();
      this.setState({
        docFilter: query,
        paginating: true,
        searching: !!query,
        filtering: !!query,
        latestSearch: requestId,
      });
      this.debounceSearchDocs(query, requestId);
    }
  }

  debounceHandleSearch = debounce(this.handleSearch, FILTER_TABLE_DEBOUNCE);

  // Load more documents when the user has reached the end of the page
  handleScroll() {
    const { next } = this.props.docs;
    const unfilteredNext = this.state.docFilter ? this.state.unfilteredNext : next;
    if (!next) {
      this.setState({ paginating: false });
      return;
    }
    if (window.innerHeight + document.documentElement.scrollTop > document.documentElement.offsetHeight - 100) {
      this.setState(() => ({
        nextPage: next,
        paginating: true,
        searching: false,
        unfilteredNextPage: unfilteredNext,
      }), () => this.props.getEsignDocs(next));
    }
  }

  buildComponentContent() {
    const {
      loading,
      docs: { results: documents },
      companyEsignTierName,
      getEsignatureDocsLoading,
    } = this.props;
    const {
      selectedCompanyId,
      paginating,
      docFilter,
      filtering,
      searching,
    } = this.state;

    if (loading[GET_ESIGN_DOCS] === 'complete' && documents.length === 0) {
      return (
        <Fragment>
          <Col sm={12}>
            <NoContent
              heading="NO_DOCUMENTS_TO_DISPLAY"
              bodyText="ADD_A_DOCUMENT_TO_GET_STARTED_WITH_E_SIGNATURES"
              buttonText="ADD_DOCUMENT"
              onClick={() => this.toggleCreateTemplateModal(false)}
            />
          </Col>
        </Fragment>
      );
    } else if (!selectedCompanyId) {
      return (
        <Row>
          <Col sm={12}>
            <Alert color="primary">
              <h4><FormattedMessage id="SELECT_A_COMPANY" /></h4>
              <p><FormattedMessage id="SELECT_A_COMPANY_VERBIAGE" /></p>
            </Alert>
          </Col>
        </Row>
      );
    }
    return (
      <Fragment>
        <Row className="pb-3 docs-toolbar">
          <Col
            xs={{ size: 12 }}
            sm={{ size: 6 }}
            md={{ size: 5 }}
            lg={{ size: 4 }}
            xl={{ size: 3 }}
            className="text-left"
          >
            <SearchBar
              name="docFilter"
              placeholder="Search Company Documents"
              keyUpHandler={this.debounceHandleSearch.bind(this)}
              loading={getEsignatureDocsLoading === 'loading'}
              searching={searching}
              filtering={filtering}
            />
          </Col>
          <Col
            xs={{ size: 12, offset: 0 }}
            sm={{ size: 4, offset: 2 }}
            md={{ size: 3, offset: 4 }}
            lg={{ size: 3, offset: 5 }}
            xl={{ size: 2, offset: 7 }}
            className="text-right my-auto"
          >
            <Button
              onClick={() => this.toggleCreateTemplateModal(false)}
              disabled={loading[GET_CREATE_TEMPLATE_URL] === 'loading'}
              color="primary"
              block
            >
              <FormattedMessage id="ADD_DOCUMENT" />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <div className="bg-white border-grey border p-4 w-100 d-flex flex-column">
              <CompanyDocumentsTable
                data={documents}
                rowAction={this.editTemplate}
                key={documents}
                sendSignatureRequestsAction={this.showSendSignatureModal}
                companyEsignTierName={companyEsignTierName}
                getEsignatureDocsLoading={getEsignatureDocsLoading}
                docFilter={docFilter}
                filtering={filtering}
                searching={searching}
              />
            </div>
          </Col>
          <Col sm={12}>
            <div className="w-100 d-flex justify-content-center">
              <h6 className="d-flex justify-content-center flex-row p-3">
                {
                  paginating
                  && getEsignatureDocsLoading === 'loading'
                  && (
                    this.state.searching ?
                      null
                      :
                      <Fragment><Spinner className="mr-1" /><FormattedMessage id="LOADING_MORE_DOCUMENTS" />...</Fragment>
                  )
                }
              </h6>
            </div>
          </Col>
        </Row>
      </Fragment>
    );
  }

  render() {
    const { target, mode, selectedCompanyId } = this.state;
    const { selectedDocument, loading, getDocCategoryList } = this.props;
    const formData = {
      initialValues: {
        title: selectedDocument.title || selectedDocument.filename,
        visibility: selectedDocument.isPublishedToEmployee,
      },
      validationSchema: Yup.object().shape({
        title: Yup.string().required('PLEASE_SPECIFY_A_TITLE'),
      }),
    };
    let signatureTypeLabel;
    if (selectedDocument) {
      const isSimplesign = !selectedDocument.isHelloSignTemplate && selectedDocument.isEsignatureDocument;
      if (selectedDocument.isHelloSignTemplate) {
        signatureTypeLabel = 'ENHANCED_ESIGNATURE';
      } else if (isSimplesign) {
        signatureTypeLabel = 'SIMPLE_ESIGNATURE';
      } else {
        signatureTypeLabel = 'NO_SIGNATURE';
      }
    }
    const tooltipContent = (
      <div>
        <p className="pt-2"><FormattedMessage id="COMPANY_DOCUMENTS_VISIBILITY_TOOLTIP" /></p>
      </div>
    );
    const formFields = [];
    formFields.push(
      {
        key: 'title',
        type: 'input',
        data: {
          colClasses: [],
          name: 'title',
          label: 'TITLE',
          placeholder: 'Title',
        },
      },
      {
        key: 'category',
        type: 'creatable',
        data: {
          dataCy: 'category',
          colClasses: [],
          name: 'category',
          label: 'CATEGORY',
          defaultOptionValue: selectedDocument.category || '',
          defaultOptionLabel: selectedDocument.category || '',
          options: this.groupCategories(),
          placeholder: 'Select a category',
          maxLength: MAX_DOC_CATEGORY_LENGTH,
          customStyles: {
            singleValue: provided => ({ ...provided, whiteSpace: 'pre !important' }),
            menu: provided => ({
              ...provided,
              whiteSpace: 'pre !important',
              maxHeight: '20vh',
              overflow: 'scroll',
            }),
            menuList: provided => ({ ...provided, maxHeight: '20vh' }),
          },
          onScrollToBottom: this.loadMoreCategories.bind(this),
          isComponentLoading: loading[GET_EMPLOYEE_DOCUMENT_CATEGORIES] === 'loading',
        },
      }
    );
    if (!selectedDocument.isEsignatureDocument) {
      formFields.push({
        key: 'visibility',
        type: 'checkbox',
        data: {
          name: 'visibility',
          id: 'visibility',
          label: 'Publish to employees',
          title: 'VISIBILITY',
          tooltipContent,
        },
      });
    }
    formFields.push({
      key: 'signature-option',
      type: 'detail',
      data: {
        title: 'SIGNATURE_OPTION',
        label: signatureTypeLabel,
        useFormattedMessage: true,
      },
    });
    let content;
    if (selectedCompanyId) {
      if (mode === 'edit') {
        content =
          (<EditDocument
            isManagerOrAbove
            isFormLocked={false}
            isDeleteButtonDisabled={false}
            onSubmit={(values) => {
              this.onSubmit(values.category, values.title, values.visibility);
            }}
            formData={formData}
            formFields={formFields}
            signatureTypeLabel={signatureTypeLabel}
            editTemplateUrl={this.props.editTemplateUrl}
            getDocCategoryList={() => getDocCategoryList()}
          />);
      } else if (mode === 'sendSignature') {
        content = <SendForSignatures isOpen={this.state.isModalOpen} toggle={() => this.toggleSendSignatureModal()} />;
      } else {
        content =
          (<CreateTemplate
            isOpen={this.state.isModalOpen}
            toggle={() => this.toggleCreateTemplateModal(true)}
          />);
      }
    }
    const component = (
      <div className="pt-4 h-100">
        {content}
        <Row>
          <Col xs={12}>
            <h1 className="h3"><FormattedMessage id="COMPANY_DOCUMENTS" /></h1>
          </Col>
        </Row>
        {this.buildComponentContent()}
      </div>
    );
    if (target) {
      return createPortal(component, target);
    }
    return null;
  }
}

CompanyDocuments.defaultProps = {
  toggleDrawer: null,
  loading: {},
  selectedDocument: {},
  sentSignatureRequests: [],
  getEsignatureDocsLoading: '',
  docCategories: '',
  drawerIsOpen: false,
  editTemplateUrl: null,
};

CompanyDocuments.propTypes = {
  editTemplateUrl: PropTypes.string,
  getTenantEsignatureData: PropTypes.func.isRequired,
  toggleDrawer: PropTypes.func,
  loading: LOADING_PROP_TYPE,
  getEsignatureDocsLoading: PropTypes.string,
  docs: PropTypes.shape({
    next: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string,
      title: PropTypes.string,
      uploadDate: PropTypes.string,
      uploadedBy: PropTypes.string,
      category: PropTypes.string,
      isEsignatureDocument: PropTypes.bool,
      isHelloSignTemplate: PropTypes.bool,
      isPublishedToEmployee: PropTypes.bool,
    })),
  }).isRequired,
  drawerIsOpen: PropTypes.bool,
  selectedDocument: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    fileName: PropTypes.string,
    category: PropTypes.string,
    uploadDate: PropTypes.string,
    extension: PropTypes.string,
    isPublishedToEmployee: PropTypes.bool,
    companyId: PropTypes.number,
    companyName: PropTypes.string,
    employeeId: PropTypes.number,
    employeeName: PropTypes.string,
    isOnboarding: PropTypes.bool,
    uploadedBy: PropTypes.string,
    isLegacyDocument: PropTypes.bool,
    isEsignatureDocument: PropTypes.bool,
    isHelloSignTemplate: PropTypes.bool,
    status: PropTypes.shape({
      name: PropTypes.string,
      priority: PropTypes.number,
      stepNumber: PropTypes.number,
      isProcessing: PropTypes.bool,
    }),
  }),
  sentSignatureRequests: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
  })),
  getCompanies: PropTypes.func.isRequired,
  getCompany: PropTypes.func.isRequired,
  getEsignDocs: PropTypes.func.isRequired,
  setSelectedDocument: PropTypes.func.isRequired,
  getDocumentPreviewUrl: PropTypes.func.isRequired,
  displayToaster: PropTypes.func.isRequired,
  clearLoadingState: PropTypes.func.isRequired,
  companyEsignTierName: PropTypes.string.isRequired,
  updateCompanyDocument: PropTypes.func.isRequired,
  docCategories: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    recent: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
    next: PropTypes.string,
  }),
  getDocCategoryList: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const {
      resources: {
        templates: {
          editTemplate: {
            url,
          },
        },
        docs: {
          esignDocs,
          selectedDocument,
          docCategories,
        },
        signatureRequest: {
          sentSignatureRequests,
        },
        companies: {
          results,
          selectedCompany: {
            esignatureProductTier: {
              name: companyEsignTierName = esignProductTierNames.SIMPLESIGN,
            },
          },
        },
      },
      appState: {
        loading,
        drawer: {
          isOpen: drawerIsOpen,
        },
      },
    } = state;
    return {
      getEsignatureDocsLoading: loading[GET_ESIGN_DOCS],
      loading,
      drawerIsOpen,
      docs: esignDocs,
      companies: results,
      selectedDocument,
      sentSignatureRequests,
      companyEsignTierName,
      docCategories,
      editTemplateUrl: url,
    };
  },
  {
    getEsignDocs,
    getCompanies,
    getCompany,
    toggleDrawer: open => toggleDrawer(open),
    setSelectedDocument: document => setSelectedDocument(document),
    getDocumentPreviewUrl,
    displayToaster: args => displayToaster(...args),
    clearLoadingState: action => clearLoadingState(action),
    getTenantEsignatureData: formatMessage => getTenantEsignatureData(formatMessage),
    updateCompanyDocument,
    getDocCategoryList: (next, companyId) => getCompanyDocCategories(next, companyId),
    getEditTemplateUrl: templateId => getEditTemplateUrl(templateId),
  }
)(hot(module)(CompanyDocuments));



// WEBPACK FOOTER //
// ./src/containers/CompanyDocuments/CompanyDocuments.jsx